import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { WorkspaceComment } from "@/models/workspace/WorkspaceComment";
import { FC } from "react";
import { cn } from "@/lib/utils";
import ViewCommentsButton from "./ViewCommentsButton";
import CommentProfilePicture from "./CommentProfilePicture";
import NestedCommentThreadComponents from "./NestedCommentThreadComponents";
import CommentComponent from "./CommentComponent";
import CustomQuillEditor from "@/pages/workspace/components/CustomQuillEditor";
import useCommentsStore from "@/stores/comments.store";
import useWorkspaceComments from "@/hooks/useWorkspaceComments";
import useUserStore from "@/stores/user.store";
import UserAvatar from "@/components/images/UserAvatar";

type CommentThreadProps = {
  comment: WorkspaceComment;
  nestLevel?: number;
};

const CommentThread: FC<CommentThreadProps> = ({ comment, nestLevel = 0 }) => {
  // Hooks
  const {
    expandedReplies,
    setExpandedReplies,
    replyingTo,
    setReplyingTo,
    filter,
  } = useCommentsStore();
  const { addCommentReply } = useWorkspaceComments();
  const { me } = useUserStore();

  // State
  const [replyInput, setReplyInput] = useState<string>("");
  const [isReplying, setIsReplying] = useState<boolean>(false);

  // Scroll & highlight state
  const location = useLocation();
  const scrollToCommentId =
    location.state?.scrollToCommentId ??
    new URLSearchParams(location.search).get("scrollToComment") ??
    undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);

  // Refs
  const replyInputRef = useRef<HTMLDivElement>(null);

  // Effect to scroll to the reply input when it appears with better positioning
  useEffect(() => {
    if (replyingTo?.id === comment.id && replyInputRef.current) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        // Get the height of any fixed comment bar at the top/bottom
        const commentBarHeight = 80; // Adjust this value based on actual comment bar height

        const scrollOptions = {
          behavior: "smooth" as ScrollBehavior,
          block: "center" as ScrollLogicalPosition,
        };

        replyInputRef.current?.scrollIntoView(scrollOptions);

        // Add extra offset to avoid the fixed comment bar
        if (window.scrollY > 0) {
          window.scrollBy({
            top: -commentBarHeight,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [replyingTo, comment.id]);

  // Effect to scroll to and highlight comment if requested by navigation state
  useEffect(() => {
    if (scrollToCommentId === comment.id && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setHighlighted(true);
      setTimeout(() => setHighlighted(false), 2000);
    }
  }, [scrollToCommentId, comment.id]);

  // Handle functions
  const handleSubmitReply = async (comment: WorkspaceComment) => {
    setIsReplying(true);
    const expandedRepliesPlaceHolder = { ...expandedReplies };
    await addCommentReply.mutateAsync({
      id: comment.id,
      content: replyInput,
      workspaceId: comment.workspaceId,
      fileId: filter !== "workspace" ? filter : undefined,
    });
    setReplyingTo(undefined);
    setReplyInput("");
    setExpandedReplies(expandedRepliesPlaceHolder);
    setIsReplying(false);
  };

  return (
    <div
      id={comment.id}
      data-comment-id={comment.id}
      ref={containerRef}
      className={`relative ${nestLevel > 0 ? "ml-4" : ""} ${
        highlighted ? "bg-warning/20 transition-colors duration-1000" : ""
      }`}
    >
      {/* Thread container */}
      <div className="relative">
        <div className="flex flex-row gap-2">
          <CommentProfilePicture comment={comment} />
          <CommentComponent comment={comment} />
        </div>
      </div>

      {/* Container for both replies and reply input */}
      <div className="relative mt-2">
        {/* Replies section */}
        {comment.replies && comment.replies.length > 0 && (
          <div
            className={cn("relative", !expandedReplies[comment.id] && "pl-8")}
          >
            {/* Show ViewCommentsButton when replies are not expanded (including single replies that were manually collapsed) */}
            {!expandedReplies[comment.id] && (
              <ViewCommentsButton comment={comment} />
            )}

            {/* Show nested replies if expanded (regardless of reply count) */}
            {expandedReplies[comment.id] && (
              <NestedCommentThreadComponents
                comment={comment}
                nestLevel={nestLevel + 1}
              />
            )}
          </div>
        )}

        {/* Reply input now appears after all nested replies */}
        {replyingTo === comment && (
          <div
            ref={replyInputRef}
            className={cn(
              "flex w-full mt-2 gap-2 z-50",
              nestLevel >= 0 && nestLevel <= 3 ? "pl-11" : ""
            )}
          >
            <UserAvatar
              user={me}
              avatarClassName="size-8 shrink-0 relative mb-auto mt-2"
            />
            <div className="grow w-full">
              <CustomQuillEditor
                className="w-full"
                placeholder="Skriv et svar..."
                value={replyInput}
                onChange={(value) => setReplyInput(value)}
                onSubmit={() => {
                  handleSubmitReply(comment);
                }}
                onCancel={() => setReplyingTo(undefined)}
                buttonMessage="Svar"
                buttonLoadingMessage="Sender..."
                disabled={isReplying}
                isQuillEditing={true}
                autoFocus={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentThread;
